import { Checkbox } from "@rio-cloud/rio-uikit";
import { useMemo, useState } from "react";
import { getAssetsByGroup } from "../../../services/memoryAssetService";
import { Asset } from "../../../models/asset";
import UptimeAssetRow from "./UptimeAssetRow";

import "../styles.scss";

interface UptimeTagRowProps {
	allAssets: Asset[];
	tagId: string;
	tagName: string;
	assetsCount: number;
	searchTerm: string;
	viewMode: "assets" | "groups";
	selectedAssetIds: Set<string>;

	onAssetsSelect: (assetIds: string[]) => void;
}

const UptimeTagRow = ({
	allAssets,
	tagId,
	tagName,
	assetsCount,
	searchTerm,
	viewMode,
	selectedAssetIds,

	onAssetsSelect,
}: UptimeTagRowProps) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const assets = useMemo(
		() => getAssetsByGroup(allAssets, tagId, searchTerm, viewMode),
		[allAssets, tagId, searchTerm, viewMode]
	);

	const tagAssetIds = useMemo(
		() => assets.map((asset) => asset.id),
		[assets]
	);

	const areAllTagAssetsSelected =
		tagAssetIds.length > 0 && tagAssetIds.every((assetId) => selectedAssetIds.has(assetId));

	const areSomeTagAssetsSelected =
		tagAssetIds.some((assetId) => selectedAssetIds.has(assetId)) && !areAllTagAssetsSelected;

	const getTagSelectedAssetIds = () =>
		tagAssetIds.filter((assetId) => selectedAssetIds.has(assetId));

	const toggleAssetSelection = (assetId: string) => {
		const tagSelection = new Set(getTagSelectedAssetIds());

		if (tagSelection.has(assetId)) {
			tagSelection.delete(assetId);
		} else {
			tagSelection.add(assetId);
		}

		onAssetsSelect(Array.from(tagSelection));
	};

	const toggleAllAssetsSelection = () => {
		const tagSelection = new Set(getTagSelectedAssetIds());
		const shouldDeselectAllTagAssets =
			tagAssetIds.length > 0 && tagAssetIds.every((assetId) => tagSelection.has(assetId));

		if (shouldDeselectAllTagAssets) {
			tagAssetIds.forEach((assetId) => tagSelection.delete(assetId));
		} else {
			tagAssetIds.forEach((assetId) => tagSelection.add(assetId));
		}

		onAssetsSelect(Array.from(tagSelection));
	};

	return assetsCount > 0 ? (
		<div
			key={tagId}
			className={`TreeNodeContainer user-select-none${isExpanded ? " open" : ""}`}
		>
			<div className="TreeNode from-group" onClick={() => setIsExpanded(!isExpanded)}>
				<Checkbox
					className="TreeCheckbox"
					checked={areAllTagAssetsSelected}
					indeterminate={areSomeTagAssetsSelected}
					onChange={(e) => {
						e.stopPropagation();
						toggleAllAssetsSelection();
					}}
				/>
				<div className="TreeLabel">
					<div className="TreeLabelName">
						<div className="TreeLabelNameText display-flex">
							<div className="TreeLabelNameTextHeadline">{tagName}</div>
							<div className="TreeLabelCount label margin-left-10 margin-right-10 label-muted label-filled label-condensed">{assetsCount}</div>
						</div>
						<div
							className={`margin-inline-10 rioglyph text-size-12 ${isExpanded ? "rioglyph-chevron-up" : "rioglyph-chevron-down"}`}
						/>
					</div>
				</div>
			</div>

			{isExpanded && assets.map((asset) => <UptimeAssetRow
				key={asset.id}
				asset={asset}
				tagName={tagName}
				isChecked={selectedAssetIds.has(asset.id)}
				onAssetSelect={toggleAssetSelection}
			/>)}
		</div>
	) : null;
};

export default UptimeTagRow;
